import { useState, useEffect } from "react";
import styles from "./ContactMe.module.scss";
import { ReactComponent as MailIcon } from "../../assets/images/mailIcon.svg";
import { ReactComponent as PhoneIcon } from "../../assets/images/phoneIcon.svg";
import { ReactComponent as MapIcon } from "../../assets/images/mapIcon.svg";
import { ReactComponent as SendIcon } from "../../assets/images/sendIcon.svg";

function validate(fields) {
  const errors = {};
  if (!fields.name.trim()) errors.name = "Full name is required.";
  else if (fields.name.trim().length < 2)
    errors.name = "Name must be at least 2 characters.";
  if (!fields.email.trim()) errors.email = "Email address is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email))
    errors.email = "Please enter a valid email.";
  if (!fields.subject) errors.subject = "Please choose a subject.";
  if (!fields.message.trim()) errors.message = "Message cannot be empty.";
  else if (fields.message.trim().length < 20)
    errors.message = "Message should be at least 20 characters.";
  return errors;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const SUBJECTS = [
  "Project Inquiry",
  "Job Opportunity",
  "Collaboration",
  "Freelance Work",
  "Just Saying Hi",
];
const MAX_MSG = 1000;

export default function ContactMe() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const [fields, setFields] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    budget: "",
    timeline: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((f) => ({ ...f, [name]: value }));
    if (touched[name]) {
      const errs = validate({ ...fields, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: errs[name] }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const errs = validate(fields);
    setErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const setSubject = (s) => {
    setFields((f) => ({ ...f, subject: s }));
    if (touched.subject) setErrors((p) => ({ ...p, subject: undefined }));
  };

  // submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {
      name: true,
      email: true,
      subject: true,
      message: true,
    };
    setTouched(allTouched);
    const errs = validate(fields);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    setSubmitted(true);
  };

  const resetForm = () => {
    setSubmitted(false);
    setFields({
      name: "",
      email: "",
      phone: "",
      company: "",
      subject: "",
      budget: "",
      timeline: "",
      message: "",
    });
    setTouched({});
    setErrors({});
  };

  const msgLen = fields.message.length;
  const msgClass =
    msgLen > MAX_MSG * 0.9 ? "limit" : msgLen > MAX_MSG * 0.7 ? "warn" : "";

  return (
    <>
      <section id="contact" className={styles["contact-section"]}>
        {/* header */}
        <div className={styles["ct-header"]}>
          <h2>Get In Touch</h2>
          <div className={styles["ct-underline"]} />
          <p>
            Have a project in mind or just want to say hello? I'd love to hear
            from you.
          </p>
        </div>

        <div className={styles["ct-grid"]}>
          {/* ── LEFT PANEL ── */}
          <div className={styles["ct-left"]}>
            {/* info card */}
            <div className={styles["ct-info-card"]}>
              <div className={styles["ct-availability"]}>
                <span className={styles["ct-avail-dot"]} />
                Available for work
              </div>
              <h3>Let's build something amazing together</h3>
              <p>
                I'm a Full Stack Engineer with 5+ years of experience. Whether
                it's a new product, a tricky bug, or a bold idea — let's talk.
              </p>

              <div className={styles["ct-contact-items"]}>
                <a
                  href="mailto:sreeragvs228@gmail.com"
                  className={styles["ct-contact-item"]}
                >
                  <div className={styles["ct-ci-icon"]}>
                    <MailIcon />
                  </div>
                  <div>
                    <div className={styles["ct-ci-label"]}>Email</div>
                    <div className={styles["ct-ci-value"]}>
                      sreeragvs228@gmail.com
                    </div>
                  </div>
                </a>
                <a
                  href="tel:+918289818525"
                  className={styles["ct-contact-item"]}
                >
                  <div className={styles["ct-ci-icon"]}>
                    <PhoneIcon />
                  </div>
                  <div>
                    <div className={styles["ct-ci-label"]}>Phone</div>
                    <div className={styles["ct-ci-value"]}>+91 8289818525</div>
                  </div>
                </a>
                <div className={styles["ct-contact-item"]}>
                  <div className={styles["ct-ci-icon"]}>
                    <MapIcon />
                  </div>
                  <div>
                    <div className={styles["ct-ci-label"]}>Location</div>
                    <div className={styles["ct-ci-value"]}>Kerala, India</div>
                  </div>
                </div>
              </div>
            </div>

            {/* response time */}
            {/* <div className={styles["ct-response-card"]}>
              <div className={styles["ct-clock-icon"]}>⚡</div>
              <div className={styles["ct-response-text"]}>
                <strong>Fast Response Guaranteed</strong>
                <span>I typically reply within 24 hours on business days.</span>
              </div>
            </div> */}
          </div>

          {/* ── RIGHT PANEL — FORM ── */}
          {/* <div className={styles["ct-form-card"]}>
            {submitted ? (
              <div className={styles["ct-success"]}>
                <div className={styles["ct-success-icon"]}>🎉</div>
                <h3>Message Sent!</h3>
                <p>
                  Thanks for reaching out,{" "}
                  <strong>{fields.name.split(" ")[0]}</strong>! I've received
                  your message and will get back to you within 24 hours.
                </p>
                <button
                  className={styles["ct-success-reset"]}
                  onClick={resetForm}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className={styles["ct-form-head"]}>
                  <h3>Send a Message</h3>
                  <p>
                    Fill in the details below and I'll get back to you shortly.
                  </p>
                </div>

                <div className={styles["ct-subject-tabs"]}>
                  {SUBJECTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`${styles["ct-subject-tab"]}${fields.subject === s ? ` ${styles["active"]}` : ""}`}
                      onClick={() => setSubject(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {errors.subject && (
                  <div
                    className={styles["ct-field-err"]}
                    style={{ marginBottom: 14, marginTop: -6 }}
                  >
                    ⚠ {errors.subject}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className={styles["ct-form-row"]}>
                    <div className={styles["ct-field"]}>
                      <label>
                        Full Name <span>*</span>
                      </label>
                      <input
                        className={`${styles["ct-input"]}${errors.name ? ` ${styles["error"]}` : ""}`}
                        type="text"
                        name="name"
                        placeholder="Sreerag V S"
                        value={fields.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.name && (
                        <div className={styles["ct-field-err"]}>
                          ⚠ {errors.name}
                        </div>
                      )}
                    </div>
                    <div className={styles["ct-field"]}>
                      <label>
                        Email Address <span>*</span>
                      </label>
                      <input
                        className={`${styles["ct-input"]}${errors.email ? ` ${styles["error"]}` : ""}`}
                        type="email"
                        name="email"
                        placeholder="contact@sreeragvs.com"
                        value={fields.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.email && (
                        <div className={styles["ct-field-err"]}>
                          ⚠ {errors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles["ct-form-row"]}>
                    <div className={styles["ct-field"]}>
                      <label>
                        Phone{" "}
                        <span style={{ color: "#bbb", fontWeight: 400 }}>
                          (optional)
                        </span>
                      </label>
                      <input
                        className={styles["ct-input"]}
                        type="tel"
                        name="phone"
                        placeholder="+91 00000 00000"
                        value={fields.phone}
                        onChange={handleChange}
                      />
                    </div>
                    <div className={styles["ct-field"]}>
                      <label>
                        Company{" "}
                        <span style={{ color: "#bbb", fontWeight: 400 }}>
                          (optional)
                        </span>
                      </label>
                      <input
                        className={styles["ct-input"]}
                        type="text"
                        name="company"
                        placeholder="Your company name"
                        value={fields.company}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className={styles["ct-extras-row"]}>
                    <div className={styles["ct-field"]}>
                      <label>Budget Range</label>
                      <select
                        className={styles["ct-select"]}
                        name="budget"
                        value={fields.budget}
                        onChange={handleChange}
                      >
                        <option value="">Select budget…</option>
                        <option>Under ₹50,000</option>
                        <option>₹50,000 – ₹1,50,000</option>
                        <option>₹1,50,000 – ₹5,00,000</option>
                        <option>₹5,00,000+</option>
                        <option>Let's discuss</option>
                      </select>
                    </div>
                    <div className={styles["ct-field"]}>
                      <label>Timeline</label>
                      <select
                        className={styles["ct-select"]}
                        name="timeline"
                        value={fields.timeline}
                        onChange={handleChange}
                      >
                        <option value="">Select timeline…</option>
                        <option>ASAP</option>
                        <option>Within 1 month</option>
                        <option>1–3 months</option>
                        <option>3–6 months</option>
                        <option>Flexible</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles["ct-field"]}>
                    <label>
                      Your Message <span>*</span>
                    </label>
                    <textarea
                      className={`${styles["ct-textarea"]}${errors.message ? ` ${styles["error"]}` : ""}`}
                      name="message"
                      placeholder="Tell me about your project, idea, or question…"
                      value={fields.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      maxLength={MAX_MSG}
                    />
                    {errors.message ? (
                      <div className={styles["ct-field-err"]}>
                        ⚠ {errors.message}
                      </div>
                    ) : (
                      <div
                        className={`${styles["ct-char-counter"]} ${styles[msgClass]}`}
                      >
                        {msgLen}/{MAX_MSG}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className={styles["ct-submit"]}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className={styles["ct-spinner"]} />{" "}
                        <span>Sending…</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span> <SendIcon />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div> */}
        </div>
      </section>
    </>
  );
}
